import { useState } from "react";
import { Wand2, Loader2, Check, ArrowRight, Lightbulb, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const fmt = (s) => [s?.new_date, s?.new_start_time && `${s.new_start_time}${s.new_end_time ? `–${s.new_end_time}` : ""}`].filter(Boolean).join(" · ");

/* AI Itinerary Optimizer — reviews the whole trip (activities + diary entries) and
   proposes moves/tips. Every "move" needs an explicit Apply; nothing changes on its own. */
export default function OptimizePanel({ tripId, readOnly, onChanged }) {
  const [wishes, setWishes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState({});

  const run = async () => {
    setBusy(true);
    setResult(null); setApplied({});
    try { setResult((await api.post(`/diary/${tripId}/optimize`, { wishes })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "AI optimizer unavailable — try again"); }
    setBusy(false);
  };

  const apply = async (s, i) => {
    try {
      await api.post(`/diary/${tripId}/optimize/apply`, {
        activity_id: s.activity_id, new_date: s.new_date,
        new_start_time: s.new_start_time, new_end_time: s.new_end_time });
      setApplied((a) => ({ ...a, [i]: true }));
      toast.success("Itinerary updated");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply this change"); }
  };

  return (
    <div data-testid="diary-optimize">
      <div className="border border-violet-300 bg-gradient-to-br from-violet-50 to-indigo-50 p-4 mb-4">
        <div className="flex items-start gap-3">
          <Wand2 className="w-7 h-7 text-violet-600 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h3 className="font-heading font-900 text-base">AI Itinerary Optimization</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              AI reads your whole plan and your diary entries — overlaps, over-packed days, backtracking,
              even "we were exhausted" notes — and proposes improvements. Nothing moves until you apply each one.
            </p>
            {!readOnly && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                <input value={wishes} onChange={(e) => setWishes(e.target.value)} data-testid="diary-optimize-wishes"
                  placeholder="Optional: what matters to you? e.g. slower mornings, more food stops…"
                  className="flex-1 min-w-[220px] border border-zinc-300 px-3 py-2 text-xs outline-none focus:ring-2 focus:ring-violet-500 bg-white" />
                <button onClick={run} disabled={busy} data-testid="diary-optimize-run"
                  className="inline-flex items-center gap-1.5 bg-violet-600 text-white px-4 py-2 text-xs font-700 disabled:opacity-50">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {busy ? "Analyzing your trip…" : "Optimize my itinerary"}
                </button>
              </div>
            )}
            {readOnly && <p className="text-xs text-amber-700 font-600 mt-2">You have view-only access — ask the trip owner for edit access to run the optimizer.</p>}
          </div>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="border border-zinc-300 bg-white p-4" data-testid="diary-optimize-assessment">
            <div className="text-[10px] font-mono uppercase tracking-wider text-violet-700 mb-1">Overall read</div>
            <p className="text-sm">{result.assessment}</p>
          </div>
          {(result.suggestions || []).map((s, i) => (
            <div key={i} className={`border bg-white p-3 ${s.type === "move" ? "border-violet-200" : "border-zinc-200"}`} data-testid={`diary-optimize-sug-${i}`}>
              <div className="flex items-start gap-2.5">
                {s.type === "move"
                  ? <CalendarClock className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" />
                  : <Lightbulb className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-700 text-sm">{s.title}</div>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.reason}</p>
                  {s.type === "move" && s.current && (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
                      <span className="bg-zinc-100 px-2 py-0.5 font-600">{s.current.title}</span>
                      <span className="text-muted-foreground">{[s.current.date, s.current.start_time].filter(Boolean).join(" · ")}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-violet-600" />
                      <span className="bg-violet-100 text-violet-800 px-2 py-0.5 font-700">{fmt(s) || "—"}</span>
                    </div>
                  )}
                </div>
                {s.type === "move" && !readOnly && (
                  applied[i]
                    ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-700 shrink-0" data-testid={`diary-optimize-applied-${i}`}><Check className="w-3.5 h-3.5" /> Applied</span>
                    : <button onClick={() => apply(s, i)} data-testid={`diary-optimize-apply-${i}`}
                        className="inline-flex items-center gap-1 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 px-2.5 py-1.5 text-xs font-700 shrink-0"><Check className="w-3.5 h-3.5" /> Apply</button>
                )}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">{result.note}</p>
        </div>
      )}
      {!result && !busy && (
        <p className="text-sm text-muted-foreground">Run the optimizer after you've planned a few activities — the more diary entries you write, the smarter the suggestions.</p>
      )}
    </div>
  );
}
